import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */

/* @jsx mdx */

import DefaultLayout from "/home/runner/work/commerce-php/commerce-php/node_modules/@adobe/gatsby-theme-aio/src/components/MDXFilter/index.js";
export const _frontmatter = {};
const layoutProps = {
  _frontmatter
};
const MDXLayout = DefaultLayout;
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">


    <h1 {...{
      "id": "technical-guidelines"
    }}>{`Technical guidelines`}</h1>
    <p>{`Core developers use this document as a reference during code reviews; some rules have corresponding code checks in the static tests.`}</p>
    <p>{`These guidelines came from many years of hard work, experience, and discussions. We strongly believe that new technical initiatives should follow these recommendations, and the existing code should be improved to meet them.`}</p>
    <h2 {...{
      "id": "text-conventions"
    }}>{`Text conventions`}</h2>
    <p>{`Use `}<a parentName="p" {...{
        "href": "https://datatracker.ietf.org/doc/html/rfc2119"
      }}>{`RFC2119`}</a>{` to interpret keywords like:`}</p>
    <ul>
      <li parentName="ul">
        <p parentName="li">{`MUST and MUST NOT`}</p>
      </li>
      <li parentName="ul">
        <p parentName="li">{`REQUIRED`}</p>
      </li>
      <li parentName="ul">
        <p parentName="li">{`SHALL and SHALL NOT`}</p>
      </li>
      <li parentName="ul">
        <p parentName="li">{`SHOULD and SHOULD NOT`}</p>
      </li>
      <li parentName="ul">
        <p parentName="li">{`RECOMMENDED`}</p>
      </li>
      <li parentName="ul">
        <p parentName="li">{`MAY`}</p>
      </li>
      <li parentName="ul">
        <p parentName="li">{`OPTIONAL`}</p>
      </li>
    </ul>
    <h2 {...{
      "id": "1-basic-programming-principles"
    }}>{`1. Basic programming principles`}</h2>
    <p>{`1.1. Function arguments SHOULD NOT be modified.`}</p>
    <p>{`1.2. Explicit return types MUST BE declared on functions.`}</p>
    <p>{`1.3. Type hints for scalar arguments SHOULD be used.`}</p>
    <p>{`1.3.1. All new PHP files MUST have strict type mode enabled by starting with `}<inlineCode parentName="p">{`declare(strict_types=1);`}</inlineCode>{`. All updated PHP files SHOULD have strict type mode enabled. PHP interfaces MAY have this declaration.`}</p>
    <h2 {...{
      "id": "2-class-design"
    }}>{`2. Class design`}</h2>
    <p>{`2.1. Object decomposition MUST follow the `}<a parentName="p" {...{
        "href": "https://en.wikipedia.org/wiki/SOLID_(object-oriented_design)"
      }}>{`SOLID principles`}</a>{`.`}</p>
    <p>{`2.2. Object instantiation`}</p>
    <p>{`2.2.1. An object MUST be ready for use after instantiation. No additional public initialization methods are allowed.`}</p>
    <p>{`Examples:`}</p>
    <p><strong parentName="p">{`Not recommended:`}</strong></p>
    <pre><code parentName="pre" {...{
        "className": "language-php"
      }}>{`<?php
/**
 * Copyright [first year code created] Adobe
 * All rights reserved.
 */

class Config
{
    private $data;

    public function init() // or load()
    {
        $this->data = $this->fileReader->load('cache.xml');
    }

    public function getValue($key)
    {
        return $this->data[$key];
    }
}
`}</code></pre>
    <p><strong parentName="p">{`Recommended:`}</strong></p>
    <pre><code parentName="pre" {...{
        "className": "language-php"
      }}>{`<?php
/**
 * Copyright [first year code created] Adobe
 * All rights reserved.
 */

class Config
{
    private $data;

    public function getValue($key)
    {
        if ($this->data === null) {
            $this->data = $this->fileReader->load('cache.xml');
        }

        return $this->data[$key];
    }
}
`}</code></pre>
    <p>{`2.2.2. Factories SHOULD be used for object instantiation instead of `}<inlineCode parentName="p">{`new`}</inlineCode>{` keyword. An object SHOULD be replaceable for testing or extensibility purposes.
Exception: `}<a parentName="p" {...{
        "href": "https://en.wikipedia.org/wiki/Data_transfer_object"
      }}>{`DTOs`}</a>{`. There is no behavior in DTOs, so there is no reason for its replaceability.
Tests can create real DTOs for stubs.
Data interfaces,  Exceptions and `}<inlineCode parentName="p">{`Zend_Db_Expr`}</inlineCode>{` are examples of DTOs.`}</p>
    <p>{`2.3. Class constructor can have only dependency assignment operations and/or argument validation operations. No other operations are allowed.`}</p>
    <p>{`2.3.1. Constructor SHOULD throw an exception when validation of an argument has failed.`}</p>
    <p>{`Example:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-php"
      }}>{`class Composite
{
    /**
     * @var RendererInterface[]
     */
    private $renderers;

    /**
     * @param RendererInterface[] $renderers
     * @throws InvalidArgumentException
     */
    public function __construct(array $renderers)
    {
        foreach ($renderers as $renderer) {
            if (!$renderer instanceof RendererInterface) {
                throw new InvalidArgumentException(
                    sprintf('Instance of the phrase renderer is expected, got %s instead.', get_class($renderer))
                );
            }
        }
        $this->renderers = $renderers;
    }
}
`}</code></pre>
    <p>{`2.3.2. Events MUST NOT be triggered in constructors.`}</p>
    <p>{`Examples:`}</p>
    <p><strong parentName="p">{`Not recommended:`}</strong></p>
    <pre><code parentName="pre" {...{
        "className": "language-php"
      }}>{`<?php
/**
 * Copyright [first year code created] Adobe
 * All rights reserved.
 */

class Config
{
    private $data;

    public function __construct($fileReader, $eventManager)
    {
        $this->data = $fileReader->read('cache.xml');
        $eventManager->dispatch('config_read_after');
    }
}
`}</code></pre>
    <p><strong parentName="p">{`Recommended:`}</strong></p>
    <pre><code parentName="pre" {...{
        "className": "language-php"
      }}>{`<?php
/**
 * Copyright [first year code created] Adobe
 * All rights reserved.
 */

class Config
{
    private $fileReader;

    private $eventManager;

    public function __construct($fileReader, $eventManager)
    {
        $this->eventManager = $eventManager;
        $this->fileReader = $fileReader;
    }

    public function getData($key)
    {
        if ($this->data === null) {
            $this->data = $this->fileReader->read('cache.xml');
            $this->eventManager->dispatch('config_read_after');
        }
        return $this->data[$key];
    }
}
`}</code></pre>
    <p>{`2.4. All dependencies MUST be requested by the most generic type that is required by the client object.`}</p>
    <p>{`Examples:`}</p>
    <p><strong parentName="p">{`Not recommended:`}</strong></p>
    <pre><code parentName="pre" {...{
        "className": "language-php"
      }}>{`interface SessionAdapterInterface
{}

RedisSessionAdapter implements SessionAdapterInterface
{}

class SessionManager
{
    public function __construct(RedisSessionAdapter $sessionAdapter)
    {}
}

// Breaks polymorphism principle, restricts what types can be passed at the runtime.
`}</code></pre>
    <p><strong parentName="p">{`Recommended:`}</strong></p>
    <pre><code parentName="pre" {...{
        "className": "language-php"
      }}>{`interface SessionAdapterInterface
{}

RedisSessionAdapter implements SessionAdapterInterface
{}

class SessionManager
{
    public function __construct(SessionAdapterInterface $sessionAdapter)
    {}
}
`}</code></pre>
    <p>{`2.5. Proxies and interceptors MUST NEVER be explicitly requested in constructors.`}</p>
    <p>{`2.6. Inheritance SHOULD NOT be used. Composition SHOULD be used for code reuse.`}</p>
    <p>{`Examples:`}</p>
    <p><strong parentName="p">{`Not recommended:`}</strong></p>
    <pre><code parentName="pre" {...{
        "className": "language-php"
      }}>{`<?php
/**
 * Copyright [first year code created] Adobe
 * All rights reserved.
 */

class AbstractController extends Action
{
    // ...
    protected function validate(
        $request
    ) {}

    protected function generateHash(
        $request
    ) {}
}

class Edit extends AbstractController
{
    public function execute()
    {
        $errors = $this->validate(
            $request
        );

        // ...

        $hash = $this->generateHash(
            $request
        );
        // ...
    }
}

// Smaller classes, one responsibility, more flexible, easy to understand, more testable.
`}</code></pre>
    <p><strong parentName="p">{`Recommended:`}</strong></p>
    <pre><code parentName="pre" {...{
        "className": "language-php"
      }}>{`<?php
/**
 * Copyright [first year code created] Adobe
 * All rights reserved.
 */

class Edit extends Action
{
    public function __constructor(
        ValidatorInterface $validator,
        HashGeneratorInterface $hashGenerator
    ) {}

    public function execute()
    {
        $errors = $this->validator->validate($request);
        // ...
        $hash = $this->hashGenerator->generateHash($request);
    }
}
`}</code></pre>
    <p>{`2.7. All non-public properties and methods SHOULD be private.`}</p>
    <p>{`2.8. Abstract classes MUST NOT be marked as public `}<inlineCode parentName="p">{`@api`}</inlineCode>{`.`}</p>
    <p>{`2.9. Service classes (ones that provide behavior but not data, like `}<inlineCode parentName="p">{`EventManager`}</inlineCode>{`) SHOULD NOT have a mutable state.`}</p>
    <p>{`2.10. Only data objects or entities (Product, Category, etc.) MAY have any observable state.`}</p>
    <p>{`2.11. "Setters" SHOULD NOT be used. They are only allowed in Data Transfer Objects.`}</p>
    <p>{`2.12. "Getters" SHOULD NOT change the state of an object.`}</p>
    <p>{`2.13. Static methods SHOULD NOT be used.`}</p>
    <p>{`2.14. Temporal coupling MUST be avoided.`}</p>
    <p>{`Example #1:`}</p>
    <p><strong parentName="p">{`Not recommended:`}</strong></p>
    <pre><code parentName="pre" {...{
        "className": "language-php"
      }}>{`$url = new Url();
$url->setBaseUrl($baseUrl);
echo $url->get('custom/path'); // prints full URL

// Developer forgot or did not know that you need to call setBaseUrl
$url = new Url();
echo $url->get('custom/path'); // Throws exception, which makes issue smaller. If it does not throw an exception, it could lead to a hidden bug more likely.

// Method with out parameters that does not return anything could be sign of temporal coupling.
`}</code></pre>
    <p><strong parentName="p">{`Recommended:`}</strong></p>
    <pre><code parentName="pre" {...{
        "className": "language-php"
      }}>{`$url = new Url($baseUrl);
echo $url->get('custom/path');

// Or
$url = new Url();
echo $url->get($baseUrl, 'custom/path');

// Only one way to use API, no temporal coupling.
`}</code></pre>
    <p>{`Example #2:`}</p>
    <p><strong parentName="p">{`Not recommended:`}</strong></p>
    <pre><code parentName="pre" {...{
        "className": "language-php"
      }}>{`<?php
/**
 * Copyright [first year code created] Adobe
 * All rights reserved.
 */

class Edit extends Action
{
    public function execute()
    {
        // ...
        $product = $productResource->load($product, $productSku, 'sku');
        $this->registry->register('product', $product);
    }
}

class View extends Template
{
    public function getProductName()
    {
        $product = $this->registry->get('product');
        return $product->getName();
    }
}
`}</code></pre>
    <p><strong parentName="p">{`Recommended:`}</strong></p>
    <pre><code parentName="pre" {...{
        "className": "language-php"
      }}>{`<?php
/**
 * Copyright [first year code created] Adobe
 * All rights reserved.
 */

class Edit extends Action
{
    public function execute()
    {
        // ...
        $product = $productRepository->get($productSku);
    }
}

class View extends Template
{
    public function getProductName()
    {
        // ...
        $product = $productRepository->get($productSku);
        return $product->getName();
    }
}
// More flexible, no dependencies between classes, no temporal coupling.
`}</code></pre>
    <p>{`2.15. Method chaining in class design MUST be avoided.`}</p>
    <p>{`2.16. `}<a parentName="p" {...{
        "href": "https://en.wikipedia.org/wiki/Law_of_Demeter"
      }}>{`Law of Demeter`}</a>{` SHOULD be obeyed.`}</p>
    <p>{`2.17. Patterns`}</p>
    <p>{`2.17.1. Proxies SHOULD be used for lazy-loading optional dependencies.`}</p>
    <p>{`2.17.2. Composites SHOULD be used when there is a need to work with a tree as a single object.`}</p>
    <p>{`Example:`}</p>
    <p>{`You need to read configuration from different sources (like database or filesystem) and want to make the reading process configurable: allow extensions to add more configuration sources. In this case, you can create a `}<inlineCode parentName="p">{`ConfigReaderInterface`}</inlineCode>{` with a composite implementation - `}<inlineCode parentName="p">{`ConfigReaderComposite`}</inlineCode>{`, and configure particular readers as children of a composite reader.`}</p>
    <p>{`2.17.3. Strategy SHOULD be used when there are multiple algorithms for performing an operation.`}</p>
    <h2 {...{
      "id": "3-dependency-injection"
    }}>{`3. Dependency injection`}</h2>
    <p>{`3.1. There SHOULD be no circular dependencies between objects.`}</p>
    <p>{`3.2. The `}<inlineCode parentName="p">{`app/etc/di.xml`}</inlineCode>{` file MUST contain only framework-level Dependency Injection (DI) settings.`}</p>
    <p>{`3.3. All modular DI settings (except for Presentation layer configuration) SHOULD be stored in `}<inlineCode parentName="p">{`<module_dir>/etc/di.xml`}</inlineCode>{`.`}</p>
    <p>{`3.4. All modular Presentation layer DI settings SHOULD be stored in `}<inlineCode parentName="p">{`<module_dir>/etc/<area_code>/di.xml`}</inlineCode>{`.`}</p>
    <h2 {...{
      "id": "4-interception"
    }}>{`4. Interception`}</h2>
    <p>{`4.1. Around-plugins SHOULD only be used when behavior of an original method is supposed to be substituted in certain scenarios.`}</p>
    <p>{`4.2. Plugins SHOULD NOT be used within own module.`}</p>
    <p>{`4.3. Plugins SHOULD NOT be added to data objects.`}</p>
    <p>{`4.4. Plugins MUST be stateless.`}</p>
    <p>{`4.5. Plugins SHOULD NOT change the state of an intercepted object (Intercepted object is `}<inlineCode parentName="p">{`$subject`}</inlineCode>{`).`}</p>
    <h2 {...{
      "id": "5-exceptions"
    }}>{`5. Exceptions`}</h2>
    <p>{`5.1. All exceptions that are surfaced to the end user MUST produce error messages in the following format:`}</p>
    <ul>
      <li parentName="ul">
        <p parentName="li">{`Symptom`}</p>
      </li>
      <li parentName="ul">
        <p parentName="li">{`Details`}</p>
      </li>
      <li parentName="ul">
        <p parentName="li">{`Solution or workaround`}</p>
      </li>
    </ul>
    <p>{`5.2. Exceptions MUST NOT be handled in the same function where they are thrown.`}</p>
    <p>{`5.3. If a function A calls function B, and function B might throw an exception, this exception MUST be either processed by function A or declared by the @throws annotation in the documentation block of function A.`}</p>
    <p>{`5.4. Exceptions MUST NOT handle message output. It is the processing code that decides how to process an exception.`}</p>
    <p>{`5.5. Business logic (both application and domain) MUST NOT be managed with exceptions. Conditional statements SHOULD be used instead.`}</p>
    <p>{`5.6. The short name of an exception class MUST be clear, meaningful, and state the cause of exception.`}</p>
    <p>{`5.7. Thrown exceptions SHOULD be as specific as possible. The top generic `}<inlineCode parentName="p">{`\\Exception`}</inlineCode>{` SHOULD NOT be thrown anywhere.`}</p>
    <p>{`5.8. All direct communications with third-party libraries MUST be wrapped with a try/catch statement.`}</p>
    <p>{`5.9. `}<inlineCode parentName="p">{`\\Exception`}</inlineCode>{` SHOULD be caught only in the code that calls third-party libraries, in addition to catching specific exceptions thrown by the library.`}</p>
    <p>{`5.10. `}<inlineCode parentName="p">{`\\Exception`}</inlineCode>{` SHOULD NOT be thrown in Front Controller and Action Controllers.`}</p>
    <p>{`5.11. A separate exceptions hierarchy SHOULD be defined on each application layer. It is allowed to throw exceptions that are only defined on the same layer.`}</p>
    <p>{`5.12. If an exception is caught on the application layer that differs from the one where it has been thrown, and it SHOULD be re-thrown, you SHOULD create a new exception instance that is appropriate for the current layer. In this case, the original exception must be passed to a new instance with the "previous" argument.`}</p>
    <p>{`5.13. It is not allowed to absorb exceptions with no logging or/and any workaround operation executed.`}</p>
    <p>{`5.14. Any exception SHOULD be logged only in the `}<inlineCode parentName="p">{`catch`}</inlineCode>{` block where it is processed, and SHOULD NOT be re-thrown.`}</p>
    <p>{`5.15. Exceptions SHOULD NOT be caught in a loop. The loop SHOULD be wrapped with a `}<inlineCode parentName="p">{`try/catch`}</inlineCode>{` construct instead.`}</p>
    <p>{`5.16. If a method uses system resources (such as files, sockets, streams, etc.), the code MUST be wrapped with a `}<inlineCode parentName="p">{`try`}</inlineCode>{` block and the corresponding `}<inlineCode parentName="p">{`finally`}</inlineCode>{` block. In the `}<inlineCode parentName="p">{`finally`}</inlineCode>{` sections, all resources SHOULD be properly released.`}</p>
    <p>{`5.17. Exceptions which need to be displayed to the user MUST be sub-types of `}<inlineCode parentName="p">{`LocalizedException`}</inlineCode>{`. Any other types of exceptions MUST be wrapped with `}<inlineCode parentName="p">{`LocalizedException`}</inlineCode>{` before being displayed to the user.`}</p>
    <p>{`5.18. `}<inlineCode parentName="p">{`LocalizedException`}</inlineCode>{`s SHOULD be thrown in the presentation layer only.`}</p>
    <p>{`5.19. Each module or component MUST declare its own exceptions. Exceptions declared in other components SHOULD NOT be thrown.`}</p>
    <p>{`5.20. Plugin MUST only throw exceptions declared by the method to which the plugin is added or derived exceptions. Observer MUST only throw exceptions declared by the method that triggers an event or derived exceptions.`}</p>
    <h2 {...{
      "id": "6-application-layers"
    }}>{`6. Application layers`}</h2>
    <h3 {...{
      "id": "61-all-layers"
    }}>{`6.1. All layers`}</h3>
    <p>{`6.1.1. Application SHOULD be structured in compliance with the `}<a parentName="p" {...{
        "href": "https://martinfowler.com/bliki/CQRS.html"
      }}>{`CQRS principle`}</a>{`.`}</p>
    <p>{`6.1.2. Every application layer (Presentation, Service Contracts, Data Access)
MUST process (handle or re-throw) exceptions of the underlying layer.`}</p>
    <p>{`6.1.3. A layer MUST NOT depend on a layer that invokes (above) it. A layer MUST only depend on a layer directly below it.
`}<span parentName="p" {...{
        "className": "gatsby-resp-image-wrapper",
        "style": {
          "position": "relative",
          "display": "block",
          "marginLeft": "auto",
          "marginRight": "auto",
          "maxWidth": "720px"
        }
      }}>{`
      `}<span parentName="span" {...{
          "className": "gatsby-resp-image-background-image",
          "style": {
            "paddingBottom": "75%",
            "position": "relative",
            "bottom": "0",
            "left": "0",
            "display": "block",
            "transition": "opacity 0.5s 0.5s",
            "pointerEvents": "none"
          }
        }}></span>{`
  `}<picture parentName="span">{`
          `}<source parentName="picture" {...{
            "srcSet": ["/commerce/php/static/3c4daa95ac9afd339f3ba2beb3c98a5f/5530d/archi_diagrams_layers_alt4.webp 320w", "/commerce/php/static/3c4daa95ac9afd339f3ba2beb3c98a5f/0c8fb/archi_diagrams_layers_alt4.webp 640w", "/commerce/php/static/3c4daa95ac9afd339f3ba2beb3c98a5f/df77d/archi_diagrams_layers_alt4.webp 720w"],
            "sizes": "(max-width: 720px) 100vw, 720px",
            "type": "image/webp"
          }}></source>{`
          `}<source parentName="picture" {...{
            "srcSet": ["/commerce/php/static/3c4daa95ac9afd339f3ba2beb3c98a5f/8980b/archi_diagrams_layers_alt4.jpg 320w", "/commerce/php/static/3c4daa95ac9afd339f3ba2beb3c98a5f/56d4e/archi_diagrams_layers_alt4.jpg 640w", "/commerce/php/static/3c4daa95ac9afd339f3ba2beb3c98a5f/20801/archi_diagrams_layers_alt4.jpg 720w"],
            "sizes": "(max-width: 720px) 100vw, 720px",
            "type": "image/jpeg"
          }}></source>{`
          `}<img parentName="picture" {...{
            "className": "gatsby-resp-image-image",
            "src": "/commerce/php/static/3c4daa95ac9afd339f3ba2beb3c98a5f/20801/archi_diagrams_layers_alt4.jpg",
            "alt": "Commerce architecture layers",
            "title": "Commerce architecture layers",
            "loading": "lazy",
            "style": {
              "width": "100%",
              "height": "100%",
              "margin": "0",
              "verticalAlign": "middle",
              "position": "absolute",
              "opacity": "0",
              "transition": "opacity 0.5s",
              "color": "inherit",
              "boxShadow": "inset 0px 0px 0px 400px none",
              "top": "0",
              "left": "0"
            }
          }}></img>{`
        `}</picture>{`
    `}</span></p>
    <h3 {...{
      "id": "62-presentation-layer"
    }}>{`6.2. Presentation layer`}</h3>
    <p>{`6.2.1. According to CQRS, the Presentation layer hosts the Command and the Query Infrastructures:`}</p>
    <ul>
      <li parentName="ul">
        <p parentName="li"><strong parentName="p">{`Command`}</strong>{` for Actions`}</p>
      </li>
      <li parentName="ul">
        <p parentName="li"><strong parentName="p">{`Query`}</strong>{` for Layout and its elements (Blocks and UI Components)`}</p>
      </li>
    </ul>
    <p>{`6.2.2. Request, Response, Session, Store Manager and Cookie objects MUST be used only in the Presentation layer.`}</p>
    <p>{`6.2.3. All actions MUST return the `}<inlineCode parentName="p">{`ResultInterface`}</inlineCode>{` implementation.`}</p>
    <p>{`6.2.4. Actions MUST NOT reference blocks declared in layout.`}</p>
    <p>{`6.2.5 Blocks MUST NOT assume that a specific, or any, controller has been invoked for current request.`}</p>
    <p>{`6.2.6 Templates MUST NOT instantiate objects. All objects MUST be passed from the Block objects.`}</p>
    <h3 {...{
      "id": "63-data-access-persistence-layer"
    }}>{`6.3. Data Access (Persistence) layer`}</h3>
    <p>{`6.3.1. Entities MAY have fields scoped differently (in product, EAV --- per store, options --- per website).`}</p>
    <p>{`6.3.2. Every persistence operation MUST be performed with one scope set.`}</p>
    <p>{`6.3.3. Entities MUST NOT contain persistence-related logic.`}</p>
    <p>{`6.3.4. MySQL's `}<inlineCode parentName="p">{`strict_mode`}</inlineCode>{` variable SHOULD be aligned with the default `}<inlineCode parentName="p">{`strict_mode`}</inlineCode>{` of the latest MySQL release.`}</p>
    <h3 {...{
      "id": "64-service-contracts-application-layer"
    }}>{`6.4. Service Contracts (Application) layer`}</h3>
    <p>{`6.4.1. Location of API interfaces`}</p>
    <p>{`6.4.1.1. Service contract interfaces SHOULD be placed in separate API modules, except when an existing module already contains Service Contracts in the `}<inlineCode parentName="p">{`Api`}</inlineCode>{` folder. Other modules will depend on the API module, and implementations could be easily swapped via `}<inlineCode parentName="p">{`di.xml`}</inlineCode>{`. API module names must end with the `}<inlineCode parentName="p">{`Api`}</inlineCode>{` suffix. For example, if a module is named `}<inlineCode parentName="p">{`MyModule`}</inlineCode>{`, its APIs SHOULD be declared in a module named `}<inlineCode parentName="p">{`MyModuleApi`}</inlineCode>{`.`}</p>
    <p>{`6.4.1.2. Service interfaces that should be exposed as web APIs MUST be placed under the `}<inlineCode parentName="p">{`MyModuleApi/Api`}</inlineCode>{` directory. Service data interfaces MUST be placed under `}<inlineCode parentName="p">{`MyModuleApi/Api/Data`}</inlineCode>{`. Directories under `}<inlineCode parentName="p">{`MyModuleApi/Api`}</inlineCode>{` SHOULD NOT be nested.`}</p>
    <p>{`6.4.1.3. All other APIs, including explicit extension points such as Chain or Composite implementations, MUST be placed under `}<inlineCode parentName="p">{`MyModuleApi/Model`}</inlineCode>{`.`}</p>
    <p>{`6.4.2. Service Interface Structure`}</p>
    <p>{`6.4.2.1. Methods that have similar names MUST serve similar purposes across different services, but they still MAY have different signatures.`}</p>
    <p>{`6.4.2.2. Service contracts SHOULD NOT be used for read scenarios on the storefront. Instead, GraphQL SHOULD be used for storefront scenarios. Check out `}<a parentName="p" {...{
        "href": "/commerce/php/architecture/technical-vision/web-api/"
      }}>{`web API technical vision`}</a>{` for more details.`}</p>
    <p>{`6.4.2.3. Each service interface SHOULD declare a single public method. An interface name SHOULD reflect the task or action to be performed. For example, `}<inlineCode parentName="p">{`Magento\\InventoryApi\\Api\\StockSourceLinksDeleteInterface::execute(array $links)`}</inlineCode>{`. The only exception is a Repository API, which MAY be added for convenience and MUST be limited to singular CRUD operations and `}<inlineCode parentName="p">{`getList($searchCriteria)`}</inlineCode>{`.`}</p>
    <p>{`6.4.3. Service Method Signature`}</p>
    <p>{`6.4.3.1. Strict typing is enforced for Service and Data interfaces located under `}<inlineCode parentName="p">{`MyCompany/MyModuleApi/Api`}</inlineCode>{`. Only the following types are allowed:`}</p>
    <ul>
      <li parentName="ul">
        <p parentName="li">{`Scalar types: `}<inlineCode parentName="p">{`string`}</inlineCode>{` (including Date and DateTime); `}<inlineCode parentName="p">{`int`}</inlineCode>{`; `}<inlineCode parentName="p">{`float`}</inlineCode>{`; `}<inlineCode parentName="p">{`boolean`}</inlineCode></p>
      </li>
      <li parentName="ul">
        <p parentName="li">{`Data interfaces`}</p>
      </li>
      <li parentName="ul">
        <p parentName="li">{`One-dimensional indexed arrays of scalars or data interfaces: for example `}<inlineCode parentName="p">{`string[]`}</inlineCode>{`, `}<inlineCode parentName="p">{`\\MyCompany\\MyModuleApi\\Api\\Data\\SomeInterface[]`}</inlineCode>{`. Hash maps (associative arrays) are not supported.`}</p>
      </li>
      <li parentName="ul">
        <p parentName="li">{`Nullable scalars or data interfaces: for example `}<inlineCode parentName="p">{`string|null`}</inlineCode>{`. Using just `}<inlineCode parentName="p">{`null`}</inlineCode>{` is prohibited.`}</p>
      </li>
      <li parentName="ul">
        <p parentName="li"><inlineCode parentName="p">{`void`}</inlineCode></p>
      </li>
    </ul>
    <p>{`6.4.3.2. Service contracts SHOULD support batch data processing. For example, an entity persisting method SHOULD accept an array of entities to persist instead of a single entity. Customizations implemented through plugins SHOULD be adjusted respectively.`}</p>
    <p>{`6.4.3.3. Batch retrieval operations MUST accept `}<inlineCode parentName="p">{`SearchCriteriaInterface`}</inlineCode>{` and return `}<inlineCode parentName="p">{`SearchResultInterface`}</inlineCode>{` to support pagination.`}</p>
    <p>{`6.4.3.4. Batch operations that modify state MUST accept an array of entities and return a response object that contains:`}</p>
    <ul>
      <li parentName="ul">
        <p parentName="li">{`An array of successfully processed items`}</p>
      </li>
      <li parentName="ul">
        <p parentName="li">{`An array of items with retriable errors`}</p>
      </li>
      <li parentName="ul">
        <p parentName="li">{`An array of items with non-retriable errors`}</p>
      </li>
    </ul>
    <p>{`6.4.3.5. Batch operations that modify state SHOULD be implemented in the most performant manner and SHOULD NOT load modified entities to generate response.`}</p>
    <p>{`6.4.3.6. Asynchronous invocation of the command services SHOULD be supported by the web API framework.`}</p>
    <p>{`6.4.3.7. Operation UUID MAY be provided by the client during service invocation. UUID MUST allow the client to get the operation status information.`}</p>
    <p>{`6.4.3.8. Data objects returned by service contracts SHOULD be fully loaded to ensure consistency.`}</p>
    <p>{`6.4.3.9. Service contracts SHOULD allow client side generated IDs. A service SHOULD accept an ID instead of generating it. See `}<a parentName="p" {...{
        "href": "https://en.wikipedia.org/wiki/Universally_unique_identifier"
      }}>{`UUID`}</a>{` as an example of client side generated ID.`}</p>
    <p>{`6.4.4. Service Implementation`}</p>
    <p>{`6.4.4.1. Service data interfaces SHOULD extend from `}<inlineCode parentName="p">{`Magento\\Framework\\Api\\ExtensibleDataInterface`}</inlineCode>{`. The only exception is when extensibility is not desired, such as in case of value-objects.`}</p>
    <p>{`6.4.4.2. Extensible data interfaces MUST NOT form hierarchies. If interface `}<inlineCode parentName="p">{`MyInterface`}</inlineCode>{` extends `}<inlineCode parentName="p">{`ExtensibleDataInterface`}</inlineCode>{`, there must be no interfaces extending `}<inlineCode parentName="p">{`MyInterface`}</inlineCode>{`. Otherwise, a list of extension attributes will be shared for all extensible interfaces in the hierarchy.`}</p>
    <p>{`6.4.4.3. Service implementations and plugins MUST NOT rely on storage-specific integrity features, such as foreign key constraints.`}</p>
    <p>{`6.4.4.4. Replacement strategy SHOULD be used to persist main entity fields/attributes, child entities, and relation links.`}</p>
    <p>{`6.4.4.5. During update operations, web APIs using the `}<inlineCode parentName="p">{`PATCH`}</inlineCode>{` HTTP method and all action controllers that accept entities SHOULD pre-load them first, then merge the request data, and provide the full data to the service.`}</p>
    <p>{`6.4.4.6. If a service method needs to modify the argument, the original argument object MUST NOT be modified and its copy SHOULD be modified instead.`}</p>
    <p>{`6.4.4.7. Services SHOULD NOT apply ACL rules to methods or returned data.`}</p>
    <p>{`6.4.4.8. If a store has multiple scopes (websites, stores), then each call MUST persist an entity in a single scope only. If an entity needs to be saved in multiple scopes, then multiple calls SHOULD be made.`}</p>
    <p>{`6.4.4.9. Service contracts SHOULD NOT apply presentation layer formatting to the returned data.`}</p>
    <p>{`6.4.4.10. Service data interfaces MUST NOT contain any business logic. They SHOULD represent a container of data that is transferable over the wire. All the business logic SHOULD be moved to services.`}</p>
    <p>{`6.4.4.11. Domain/business logic MUST be executed on the service contracts layer.`}</p>
    <p>{`6.4.4.12. Any customizations to the domain/business logic MUST be executed on the Service Contracts layer, and so MUST be declared in the `}<inlineCode parentName="p">{`global`}</inlineCode>{` area of configuration.`}</p>
    <p>{`6.4.4.13. A service contract MUST NOT rely on the execution context (application area). The service implementation MUST NOT depend on the application state.`}</p>
    <p>{`6.4.4.14. A service contract SHOULD be an `}<a parentName="p" {...{
        "href": "https://datatracker.ietf.org/doc/html/rfc7231#section-4.2.2"
      }}>{`idempotent method`}</a>{`.`}</p>
    <h2 {...{
      "id": "7-configuration"
    }}>{`7. Configuration`}</h2>
    <p>{`7.1. An Application Instance consists of:`}</p>
    <ul>
      <li parentName="ul">
        <p parentName="li">{`Code`}</p>
      </li>
      <li parentName="ul">
        <p parentName="li">{`Environment Configuration`}</p>
      </li>
      <li parentName="ul">
        <p parentName="li">{`Data`}</p>
      </li>
    </ul>
    <p>{`7.2. Code includes:`}</p>
    <ul>
      <li parentName="ul">
        <p parentName="li">{`application codebase`}</p>
      </li>
      <li parentName="ul">
        <p parentName="li">{`XML configuration`}</p>
      </li>
      <li parentName="ul">
        <p parentName="li">{`generated code and static files`}</p>
      </li>
      <li parentName="ul">
        <p parentName="li">{`database structure`}</p>
      </li>
      <li parentName="ul">
        <p parentName="li">{`system configuration values`}</p>
      </li>
      <li parentName="ul">
        <p parentName="li">{`configuration scopes (stores/store groups/websites)`}</p>
      </li>
      <li parentName="ul">
        <p parentName="li">{`CMS entities`}</p>
      </li>
    </ul>
    <p>{`7.3. Environment Configuration includes information about application services connection.`}</p>
    <p>{`7.4. Data includes the business entity data.`}</p>
    <p>{`7.5. Code and Environment Configuration MUST not be stored in Data Storage.`}</p>
    <p>{`7.6. Installation process MUST NOT modify Code.`}</p>
    <p>{`7.7. All XML configuration formats MUST be declarative. Imperative nodes are not allowed.`}</p>
    <p>{`7.8. All Configuration objects MUST use `}<inlineCode parentName="p">{`Magento\\Framework\\Config`}</inlineCode>{`.`}</p>
    <h2 {...{
      "id": "8-modularity"
    }}>{`8. Modularity`}</h2>
    <p>{`8.1. The Application Framework (`}<inlineCode parentName="p">{`Magento\\Framework\\*`}</inlineCode>{`) MUST NOT depend on application modules.`}</p>
    <p>{`8.2. All dependencies MUST be declared in the component's `}<inlineCode parentName="p">{`composer.json`}</inlineCode>{` file.`}</p>
    <p>{`8.3. If component A uses behavior of Component B, such Component B MUST be declared in the `}<inlineCode parentName="p">{`require`}</inlineCode>{` section of Component A's `}<inlineCode parentName="p">{`composer.json`}</inlineCode>{` file, except for cases where Component B is used in the code that customizes the behavior of Component B.`}</p>
    <p>{`8.4. If component A extends/customizes the behavior of component B through its customization points (layout handles, plugins, events, etc.), such Component B MUST be declared in the `}<inlineCode parentName="p">{`suggest`}</inlineCode>{` section of Component A.`}</p>
    <p>{`8.5. Only the `}<inlineCode parentName="p">{`@api`}</inlineCode>{` code of any module can be referenced by other modules.`}</p>
    <p>{`8.6. A module MUST NOT contain references to theme resources.`}</p>
    <p>{`8.7. A component MUST NOT rely either on dependencies of dependencies or on dependencies of the project it is included in (e.g., Adobe Commerce or Magento Open Source application). All component dependencies MUST be stated explicitly.`}</p>
    <h2 {...{
      "id": "9-browser-server-interaction-in-web-application"
    }}>{`9. Browser-Server interaction in web application`}</h2>
    <p>{`9.1. All Client-Server calls must follow the `}<a parentName="p" {...{
        "href": "https://en.wikipedia.org/wiki/Hypertext_Transfer_Protocol"
      }}>{`HTTP protocol`}</a>{`.`}</p>
    <p>{`9.2. All customer-agnostic data (Products, Categories, CMS Pages) MUST be rendered on a server and cached in a public cache server (Varnish).`}</p>
    <p>{`9.3. All customer-specific data MUST be rendered on the browser side using a JavaScript (JS) application.`}</p>
    <p>{`9.4. HTML markup generated on server MUST NOT contain user-specific data.`}</p>
    <p>{`9.5. HTML markup generated on server MUST NOT contain session-specific data (e.g. a form element with a CSRF token).`}</p>
    <p>{`9.6. A JS application MAY receive customer-specific data using the CustomerData JS API.`}</p>
    <p>{`9.7. All state-modifying requests from a browser SHOULD be performed with AJAX requests.`}</p>
    <p>{`9.8. If an error occurs during request handling, the server MUST return an appropriate `}<a parentName="p" {...{
        "href": "https://www.w3.org/Protocols/rfc2616/rfc2616-sec9.html"
      }}>{`HTTP status code`}</a>{` and an explanation of an error in the response body.`}</p>
    <p>{`9.9. All headers MUST be respected.`}</p>
    <p>{`9.10. The Request, Session, and Cookie objects MUST NOT be injected in an object constructor. They MUST be passed only as method arguments.`}</p>
    <p>{`9.11. Operation scopes MUST always be explicitly requested by operations (`}<inlineCode parentName="p">{`StoreManager`}</inlineCode>{` SHOULD NOT be used to retrieve the store ID).`}</p>
    <h2 {...{
      "id": "10-javascript-js-application"
    }}>{`10. JavaScript (JS) application`}</h2>
    <p>{`10.1. The UI Component framework MUST be used to build frontend applications.`}</p>
    <p>{`10.2. Only private content SHOULD be rendered in browser.`}</p>
    <p>{`10.3. All module dependencies of a RequireJS module MUST be declared in the module's definition header.`}</p>
    <p>{`10.3.1 No direct calls to `}<inlineCode parentName="p">{`require`}</inlineCode>{` SHOULD be made unless the list of modules to be loaded is dynamic.`}</p>
    <p>{`10.3.2 Code MUST NOT make use of the synchronous form of `}<inlineCode parentName="p">{`require`}</inlineCode>{` (`}<inlineCode parentName="p">{`require('moduleIdentifier')`}</inlineCode>{`).`}</p>
    <p>{`10.4. The `}<a parentName="p" {...{
        "href": "https://w3c.github.io/webappsec-csp/"
      }}>{`W3C Content Security Policy`}</a>{` MUST be followed.`}</p>
    <p>{`10.5. ESLint `}<a parentName="p" {...{
        "href": "https://github.com/magento/magento-coding-standard/blob/develop/eslint/.eslintrc-magento"
      }}>{`rules`}</a>{` SHOULD BE followed.`}</p>
    <p>{`10.5.1. ECMAScript 5.1 SHOULD be used as a JS standard.`}</p>
    <p>{`10.5.2. Language features (closures, WeakMaps, etc) MUST be used for private state. There SHOULD be no `}<inlineCode parentName="p">{`_`}</inlineCode>{` (underscore) naming convention for private properties.`}</p>
    <p>{`10.5.3. All uses of XMLHttpRequest (including jQuery's `}<inlineCode parentName="p">{`$.ajax`}</inlineCode>{`) MUST be asynchronous.`}</p>
    <p>{`10.5.4. New global properties MUST not be added (either through explicit `}<inlineCode parentName="p">{`window`}</inlineCode>{` assignment or `}<inlineCode parentName="p">{`var`}</inlineCode>{` in the top scope). The RequireJS module system SHOULD be used for shared objects.`}</p>
    <p>{`10.5.5. Modules MUST NOT have external side effects.`}</p>
    <p>{`10.5.6. Code MUST NOT re-declare any identifiers already declared in a reachable scope (re-assignment is acceptable).`}</p>
    <h2 {...{
      "id": "11-testing"
    }}>{`11. Testing`}</h2>
    <h3 {...{
      "id": "111-white-box-testing-unit-integration-functional"
    }}>{`11.1. White-box testing (unit, integration, functional)`}</h3>
    <p>{`11.1.1. Only public methods SHOULD be tested. Private and protected behavior SHOULD be tested through public methods.`}</p>
    <h3 {...{
      "id": "112-unit-testing"
    }}>{`11.2. Unit testing`}</h3>
    <p>{`11.2.1. All objects SHOULD be tested in isolation.`}</p>
    <p>{`11.2.2. `}<inlineCode parentName="p">{`ObjectManager`}</inlineCode>{` MUST NOT be used in unit tests.`}</p>
    <p>{`11.2.3. `}<inlineCode parentName="p">{`ObjectManagerHelper`}</inlineCode>{` MAY BE used to automatically mock all dependencies of the object under test.`}</p>
    <h3 {...{
      "id": "113-functional-testing"
    }}>{`11.3. Functional Testing`}</h3>
    <h4 {...{
      "id": "1131-pages"
    }}>{`11.3.1. Pages`}</h4>
    <p>{`11.3.1.1. Page file names MUST follow this pattern:`}</p>
    <ul>
      <li parentName="ul"><inlineCode parentName="li">{`{Admin or Storefront}{Description}Page.xml`}</inlineCode>{`, where `}<inlineCode parentName="li">{`{Description}`}</inlineCode>{` briefly describes the page under test.`}</li>
      <li parentName="ul">{`Use `}<a parentName="li" {...{
          "href": "http://wiki.c2.com/?PascalCase"
        }}>{`PascalCase`}</a>{`.`}</li>
      <li parentName="ul">{`Example: `}<inlineCode parentName="li">{`AdminProductAttributeGridPage.xml`}</inlineCode></li>
    </ul>
    <p>{`11.3.1.2. Page `}<inlineCode parentName="p">{`name`}</inlineCode>{` attribute MUST be the same as the file name.`}</p>
    <p>{`11.3.1.3. Page `}<inlineCode parentName="p">{`module`}</inlineCode>{` attribute MUST follow this pattern:`}</p>
    <ul>
      <li parentName="ul"><inlineCode parentName="li">{`{VendorName}_{ModuleName}`}</inlineCode></li>
      <li parentName="ul">{`Example: `}<inlineCode parentName="li">{`Magento_Backend`}</inlineCode></li>
    </ul>
    <p>{`11.3.1.4. There MUST be only one `}<inlineCode parentName="p">{`<page>`}</inlineCode>{` entity per file.`}</p>
    <h4 {...{
      "id": "1132-sections"
    }}>{`11.3.2. Sections`}</h4>
    <p>{`11.3.2.1. Section file names MUST follow this pattern:`}</p>
    <ul>
      <li parentName="ul"><inlineCode parentName="li">{`{Admin or Storefront}{Description}Section.xml`}</inlineCode>{`, where `}<inlineCode parentName="li">{`{Description}`}</inlineCode>{` briefly describes the section under test.`}</li>
      <li parentName="ul">{`Use `}<a parentName="li" {...{
          "href": "http://wiki.c2.com/?PascalCase"
        }}>{`PascalCase`}</a>{`.`}</li>
      <li parentName="ul">{`Example: `}<inlineCode parentName="li">{`StorefrontCheckoutCartSummarySection.xml`}</inlineCode></li>
    </ul>
    <p>{`11.3.2.2. Section `}<inlineCode parentName="p">{`name`}</inlineCode>{` attribute MUST be the same as the file name.`}</p>
    <p>{`11.3.2.3. There MUST be only one `}<inlineCode parentName="p">{`<section>`}</inlineCode>{` entity per file.`}</p>
    <h4 {...{
      "id": "1133-elements"
    }}>{`11.3.3. Elements`}</h4>
    <p>{`11.3.3.1. All element selectors MUST follow these `}<a parentName="p" {...{
        "href": "https://developer.adobe.com/commerce/testing/functional-testing-framework/test-writing/best-practices/"
      }}>{`best practices`}</a>{`.`}</p>
    <p>{`11.3.3.2. The element `}<inlineCode parentName="p">{`name`}</inlineCode>{` MUST be unique within the `}<inlineCode parentName="p">{`<section>`}</inlineCode>{`.`}</p>
    <p>{`11.3.3.3. The element `}<inlineCode parentName="p">{`name`}</inlineCode>{` SHOULD be written in `}<a parentName="p" {...{
        "href": "http://wiki.c2.com/?CamelCase"
      }}>{`camelCase`}</a>{`.`}</p>
    <p>{`11.3.3.4. Parameterized selectors MUST use descriptive names for their parameters.`}</p>
    <p>{`11.3.3.5. Elements SHOULD use the `}<inlineCode parentName="p">{`timeout`}</inlineCode>{` attribute to wait after interactions.`}</p>
    <h4 {...{
      "id": "1134-data-entities"
    }}>{`11.3.4. Data Entities`}</h4>
    <p>{`11.3.4.1. Data entity file names MUST follow this pattern:`}</p>
    <ul>
      <li parentName="ul"><inlineCode parentName="li">{`{Type}Data.xml`}</inlineCode>{`, where `}<inlineCode parentName="li">{`{Type}`}</inlineCode>{` describes the type of entities.`}</li>
      <li parentName="ul">{`Use `}<a parentName="li" {...{
          "href": "http://wiki.c2.com/?PascalCase"
        }}>{`PascalCase`}</a>{`.`}</li>
      <li parentName="ul">{`Examples: `}<inlineCode parentName="li">{`ProductData.xml`}</inlineCode>{` or `}<inlineCode parentName="li">{`CustomerData.xml`}</inlineCode></li>
    </ul>
    <p>{`11.3.4.2. Data entities SHOULD make use of `}<inlineCode parentName="p">{`unique="suffix"`}</inlineCode>{` or `}<inlineCode parentName="p">{`unique="prefix"`}</inlineCode>{` to ensure that tests using the entity can be repeatedly ran against the same environment.`}</p>
    <p>{`11.3.4.3. Changes to existing data entities MUST be compatible with existing tests.`}</p>
    <h4 {...{
      "id": "1135-action-groups"
    }}>{`11.3.5. Action groups`}</h4>
    <p>{`11.3.5.1. Action group file names MUST follow this pattern:`}</p>
    <ul>
      <li parentName="ul">{`If the action group is making an assertion, then use the following format: `}<inlineCode parentName="li">{`Assert{Admin or Storefront}{Functionality}ActionGroup.xml`}</inlineCode>{` where `}<inlineCode parentName="li">{`{Functionality}`}</inlineCode>{` briefly describes what the action group is doing.`}</li>
      <li parentName="ul">{`Otherwise use this format: `}<inlineCode parentName="li">{`{Admin or Storefront}{Functionality}ActionGroup.xml`}</inlineCode></li>
      <li parentName="ul">{`Example: `}<inlineCode parentName="li">{`AssertStorefrontMinicartContainsProductActionGroup.xml`}</inlineCode></li>
    </ul>
    <p>{`11.3.5.2. Action group arguments MUST specify the `}<inlineCode parentName="p">{`type`}</inlineCode>{` attribute.`}</p>
    <p>{`11.3.5.3. Action groups MUST NOT have unused arguments.`}</p>
    <p>{`11.3.5.4. Action groups MUST NOT reference created data entities such as `}<inlineCode parentName="p">{`$$createdOutOfScopeData.property$$`}</inlineCode>{` or `}<inlineCode parentName="p">{`$createdOutOfScopeData.property$`}</inlineCode>{` that were created from outside of the action group scope. Instead, action groups MUST use arguments to access this out of scope data.`}</p>
    <p>{`11.3.5.5. Action group arguments SHOULD specify default values.`}</p>
    <h4 {...{
      "id": "1136-metadata"
    }}>{`11.3.6. Metadata`}</h4>
    <p>{`11.3.6.1. Metadata file names MUST follow this pattern:`}</p>
    <ul>
      <li parentName="ul"><inlineCode parentName="li">{`{Type}Meta.xml`}</inlineCode>{`, where `}<inlineCode parentName="li">{`{Type}`}</inlineCode>{` describes the type of entity.`}</li>
      <li parentName="ul">{`Use `}<a parentName="li" {...{
          "href": "http://wiki.c2.com/?PascalCase"
        }}>{`PascalCase`}</a>{`.`}</li>
      <li parentName="ul">{`Examples: `}<inlineCode parentName="li">{`CategoryMeta.xml`}</inlineCode>{` and `}<inlineCode parentName="li">{`ProductAttributeMeta.xml`}</inlineCode></li>
    </ul>
    <h2 {...{
      "id": "12-web-api"
    }}>{`12. Web API`}</h2>
    <p>{`12.1. Both REST and SOAP APIs MUST be exposed.`}</p>
    <p>{`12.2. All Web API GET endpoints MUST return lists of entities.`}</p>
    <h2 {...{
      "id": "13-command-line-interface-cli"
    }}>{`13. Command line interface (CLI)`}</h2>
    <p>{`13.1. `}<a parentName="p" {...{
        "href": "/commerce/php/development/cli-commands/naming-guidelines/"
      }}>{`CLI command naming guidelines`}</a>{` MUST be followed.`}</p>
    <p>{`13.2. A CLI command MUST be created for any functionality intended to be used by a system integrator/system administrator/developer (for example: change indexer mode, generate a configuration file, etc.).`}</p>
    <p>{`13.3. A CLI command MUST always run in a global area. If a command needs a specific area to perform its functions, such area SHOULD be set up before execution.`}</p>
    <p>{`13.4. Exception in a single CLI command SHOULD NOT break the CLI framework; running other commands SHOULD still be possible.`}</p>
    <h2 {...{
      "id": "14-events"
    }}>{`14. Events`}</h2>
    <p>{`14.1. All values (including objects) passed to an event MUST NOT be modified in the event observer. Instead, plugins SHOULD BE used for modifying the input or output of a function.`}</p>
    <p>{`Example:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-php"
      }}>{`use Magento\\Framework\\App\\DataObject;
use Magento\\Framework\\Event\\Observer;
use Magento\\Framework\\Event\\ObserverInterface;

class SampleEventObserverThatModifiesInputs implements ObserverInterface
{
    /**
     * @param Observer $observer
     */
    public function execute(Observer $observer)
    {
        /** @var DataObject $transport */
        $transport = $observer->getData('transport');

        if ($transport->getData('some_value') === true) {
            /**
             * Expecting this value to go back to the original event dispatcher violates
             * this rule. Other observers could change the data, or Magento could make
             * architectural changes always sending immutable objects.
             */
            $transport->setData('output_return_value', true);
        }
    }
}
`}</code></pre>
    <p>{`14.2. Events used SHOULD be observed as specifically as possible. A `}<inlineCode parentName="p">{`global`}</inlineCode>{` subscription to an event SHOULD NOT be used when the area impacted is just `}<inlineCode parentName="p">{`frontend`}</inlineCode>{`.`}</p>
    <p>{`14.3. Events SHOULD NOT change a state of observable objects.`}</p>
    <h2 {...{
      "id": "15-security"
    }}>{`15. Security`}</h2>
    <p>{`15.1. Use prepared statements for SQL queries.`}</p>
    <p>{`15.2. Broken Authentication protection.`}</p>
    <p>{`15.2.1. Where possible, implement multi-factor authentication to prevent automated, credential stuffing, brute force, and stolen credential re-use attacks.`}</p>
    <p>{`15.2.2. Do not ship or deploy with any default credentials, particularly for admin users.`}</p>
    <p>{`15.2.3. Implement weak-password checks, such as testing new or changed passwords against a list of the `}<a parentName="p" {...{
        "href": "https://github.com/danielmiessler/SecLists/tree/master/Passwords"
      }}>{`top 10000 worst passwords`}</a>{`.`}</p>
    <p>{`15.2.4. Align password length, complexity, and rotation policies with `}<a parentName="p" {...{
        "href": "https://pages.nist.gov/800-63-3/sp800-63b.html#memsecret"
      }}>{`NIST 800-63 B's guidelines in section 5.1.1 for Memorized Secrets`}</a>{` or other modern, evidence-based password policies.`}</p>
    <p>{`15.2.5. Ensure registration, credential recovery, and API pathways are hardened against account enumeration attacks by using the same messages for all outcomes.`}</p>
    <p>{`15.2.6. Limit or increasingly delay failed login attempts. Log all failures and alert administrators when credential stuffing, brute force, or other attacks are detected.`}</p>
    <p>{`15.2.7. Use a server-side, secure, built-in session manager that generates a new random session ID with high entropy after login. Session IDs should not be in the URL, be securely stored and invalidated after logout, idle, and absolute timeouts.`}</p>
    <p>{`15.3. Cross-Site Scripting (XSS) protection.`}</p>
    <p>{`15.3.1. Sanitize input; escape output.`}</p>
    <p>{`15.3.2. Follow `}<a parentName="p" {...{
        "href": "/commerce/php/development/security/cross-site-scripting/"
      }}>{`XSS prevention strategies guidelines`}</a>{` for escaping output.`}</p>
    <p>{`15.3.3. Incoming data should be casted to the expected type. String data should be validated/sanitized.`}</p>
    <p>{`15.3.4. Incoming string data length should be checked.`}</p>
    <p>{`15.3.5. Special characters, like null byte characters, should be dropped from Incoming string data.`}</p>
    <p>{`15.4. A module that introduces Admin Panel functionality should have ACL.`}</p>
    <p>{`15.5. Misconfiguration protection.`}</p>
    <p>{`15.5.1. Do not include/require unused libraries/frameworks.`}</p>
    <p>{`15.5.2. A segmented application architecture that provides effective, secure separation between components or tenants, with segmentation, containerization, or cloud security groups (ACLs).`}</p>
    <p>{`15.5.3. Sending security directives to clients, e.g. `}<a parentName="p" {...{
        "href": "https://owasp.org/www-project-secure-headers/"
      }}>{`Security Headers`}</a>{`.`}</p>
    <p>{`15.6. Sensitive Data Exposure protection.`}</p>
    <p>{`15.6.1. Exceptions/Notices/Warnings should be caught and logged.`}</p>
    <p>{`15.6.2. Error output should not be displayed to the user. Display standard messages to inform the user.`}</p>
    <p>{`15.6.3. Logs should not be excessive, e.g. PDO exception contains MySQL credentials that should not be logged.`}</p>
    <p>{`15.7. Cross-Site Request Forgery (CSRF) protection.`}</p>
    <p>{`15.7.1. CSRF tokens mechanism should be utilized.`}</p>
    <p>{`15.7.2. All data manipulation requests should be made with POST requests.`}</p>
    <p>{`15.8. Frequently update third-party libraries used in the project/component to eliminate known vulnerabilities.`}</p>
    <p>{`15.9. Local File Inclusion (LFI) protection.`}</p>
    <p>{`15.9.1. User-submitted requests containing path and file name SHOULD NOT be trusted.`}</p>
    <p>{`15.9.2. User-submitted path and file values SHOULD be sanitized to remove dot-dot-slash from the request.`}</p>
    <p>{`15.10. Remote Code Execution (RCE) protection.`}</p>
    <p>{`15.10.1. `}<inlineCode parentName="p">{`eval()`}</inlineCode>{`, `}<inlineCode parentName="p">{`passthru()`}</inlineCode>{`, `}<inlineCode parentName="p">{`system()`}</inlineCode>{`, `}<inlineCode parentName="p">{`shell_exec()`}</inlineCode>{`, `}<inlineCode parentName="p">{`serialize()`}</inlineCode>{`, `}<inlineCode parentName="p">{`unserialize()`}</inlineCode>{`, `}<inlineCode parentName="p">{`md5()`}</inlineCode>{`, `}<inlineCode parentName="p">{`srand()`}</inlineCode>{`, `}<inlineCode parentName="p">{`mt_srand()`}</inlineCode>{` SHOULD NOT be used.`}</p>
    <p>{`15.10.2. User-submitted values SHOULD NOT be passed directly to `}<inlineCode parentName="p">{`include*()`}</inlineCode>{`, `}<inlineCode parentName="p">{`require*()`}</inlineCode>{`, `}<inlineCode parentName="p">{`create_function()`}</inlineCode>{`, `}<inlineCode parentName="p">{`fopen()`}</inlineCode>{`, `}<inlineCode parentName="p">{`preg_replace()`}</inlineCode>{`.`}</p>
    <p>{`15.10.3. Variable functions SHOULD NOT be used if the variable values are submitted by the user.`}</p>
    <p>{`15.11. Security capabilities SHOULD be implemented either on the Magento Framework level or in a dedicated module(s) and utilized by the entire application in a centralize manner.`}</p>
    <p>{`15.12. Files MUST be secured by a web server configuration (e.g., `}<inlineCode parentName="p">{`.htaccess`}</inlineCode>{` or `}<inlineCode parentName="p">{`nginx.conf`}</inlineCode>{`), except files that are intended to be publicly accessible.`}</p>
    <p>{`15.13 Presentation layer classes that access user input directly MUST NOT assume it has been validated.`}</p>
    <h2 {...{
      "id": "16-cron"
    }}>{`16. Cron`}</h2>
    <p>{`16.1. Cron job SHOULD be an `}<a parentName="p" {...{
        "href": "https://datatracker.ietf.org/doc/html/rfc7231#section-4.2.2"
      }}>{`idempotent method`}</a>{`.`}</p>
    <h2 {...{
      "id": "17-services"
    }}>{`17. Services`}</h2>
    <p>{`17.1. New features with limited customization scenarios SHOULD be implemented as a thin extension that will communicate to a service that contains business logic. This allows developers to release features independently of Magento and makes feature upgrades easier.`}</p>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      